import React, { useEffect, useMemo, useState, useCallback } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useParams, useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import Text from 'core-system/Text'
import Card from 'core-system/Card'
import Chip from 'core-system/Chip'
import Button from 'core-system/Button'
import TextField from 'core-system/TextField'
import FlexContainer from 'core-system/FlexContainer'
import Loading from 'core-system/Loading'
import palette from 'core-system/Themes/palette'
import moment from 'moment'
import { AppState } from 'redux/config/store'
import { CurrencyType } from 'features/Programs/Shared/types'
import { formatCurrency } from 'shared/CurrencyFunctions'
import { reimbursementsActions } from 'redux/reimbursements/reimbursementsSlice'
import { ReimbursementStatus } from 'redux/reimbursements/reimbursementsTypes'
import { Locations } from 'shared/Router/Locations'
import {
  getDefaultBudgetAllocations,
  getRemainingReimbursementBalance,
  executeResolveReimbursement,
  shouldShowReceiptDetailsLoading,
  shouldShowReimbursementBudgets,
  shouldPrefillBudgetAllocations,
  isApproveDisabled,
  isPartialDisabled,
} from '../utils'
import ReimbursementBudgets from './ReimbursementBudgets'
import ReceiptLink from './shared/ReceiptLink'

const statusMap: Record<ReimbursementStatus, any> = {
  CREATED: 'yellow',
  PROCESSED: 'blue',
  SUBMITTED: 'purple',
  APPROVED: 'green',
  REJECTED: 'red',
  PARTIAL: 'blue',
}

const statusDisplayMap: Record<ReimbursementStatus, string> = {
  CREATED: 'Under Review',
  PROCESSED: 'Processed',
  SUBMITTED: 'Submitted',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
  PARTIAL: 'Partial',
}

const Container = styled.div`
  padding: 2rem;
  @media (max-width: ${(p) => p.theme.breakpoints[0]}) {
    padding: 1rem;
  }
`

const BackLink = styled(Text)`
  cursor: pointer;
  color: ${palette.secondary.purple600};
  margin-bottom: 1.5rem;
  display: inline-block;

  &:hover {
    text-decoration: underline;
  }
`

const DetailsCard = styled(Card)`
  padding: 2rem;
  @media (max-width: ${(p) => p.theme.breakpoints[0]}) {
    padding: 1rem;
  }
`

const DetailRow = styled.div`
  display: flex;
  align-items: flex-start;
  padding: 1rem 0;
  border-bottom: 1px solid ${(props) => props.theme.palette.grey.grey300};
  gap: 0.5rem;

  &:last-child {
    border-bottom: none;
  }
  @media (max-width: ${(p) => p.theme.breakpoints[0]}) {
    flex-direction: column;
  }
`

const DetailLabel = styled(Text)`
  font-weight: 600;
  width: ${(props) => props.theme.pxToRem(200)};
  flex-shrink: 0;
  @media (max-width: ${(p) => p.theme.breakpoints[0]}) {
    width: auto;
  }
`

const formatExpenseType = (expenseType: string | null) => {
  if (!expenseType) return '-'
  return expenseType.charAt(0) + expenseType.slice(1).toLowerCase()
}

const ReimbursementDetailsView = React.memo(() => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const { receiptDetails, loading } = useSelector(
    (state: AppState) => state.reimbursements
  )
  const conversionRates = useSelector(
    (state: AppState) => state.application.properties
  )

  useEffect(() => {
    if (id) {
      dispatch(reimbursementsActions.getReceiptDetails(id))
    }
  }, [dispatch, id])

  const [resolving, setResolving] = useState(false)
  const [note, setNote] = useState('')
  const [budgetAllocations, setBudgetAllocations] = useState<
    Record<string, string>
  >({})

  const hasIdAndDetails = id != null && receiptDetails != null
  const isCurrentReceipt = hasIdAndDetails && receiptDetails.id === id
  const receipt = isCurrentReceipt ? receiptDetails : null

  const remainingReimbursementBalance = useMemo(() => {
    if (!receipt?.eligibleBudgets?.length) return null
    return getRemainingReimbursementBalance(
      receipt.amount,
      receipt.eligibleBudgets,
      budgetAllocations
    )
  }, [receipt, budgetAllocations])

  useEffect(() => {
    setBudgetAllocations({})
  }, [id])

  useEffect(() => {
    setNote(receipt?.note ?? '')
  }, [receipt])

  // Prefill allocation fields by priority when receipt is under review and has eligible budgets
  useEffect(() => {
    if (!shouldPrefillBudgetAllocations(receipt, id, budgetAllocations)) return
    setBudgetAllocations(
      getDefaultBudgetAllocations(receipt.amount, receipt.eligibleBudgets)
    )
  }, [id, receipt, budgetAllocations])

  const handleResolve = useCallback(
    async (status: ReimbursementStatus) => {
      if (!id || !receipt) return
      setResolving(true)
      const result = await executeResolveReimbursement(
        id,
        receipt,
        budgetAllocations,
        status,
        note
      )
      if (result.status === 'success') {
        dispatch(reimbursementsActions.getAllEmployeeSubmissions())
        navigate(Locations.ReimbursementSubmissions)
      } else {
        setResolving(false)
      }
    },
    [id, receipt, budgetAllocations, note, dispatch, navigate]
  )

  if (shouldShowReceiptDetailsLoading(loading, receiptDetails, id)) {
    return <Loading fullPage />
  }

  if (!receipt) {
    return (
      <Container>
        <BackLink
          variant='body1'
          onClick={() => navigate(Locations.ReimbursementSubmissions)}
        >
          &larr; Back to Reimbursement Submissions
        </BackLink>
        <Text variant='h5'>Reimbursement not found.</Text>
      </Container>
    )
  }

  const employeeName = `${receipt.employeeFirstName} ${receipt.employeeLastName}`

  return (
    <Container>
      <BackLink
        variant='body1'
        onClick={() => navigate(Locations.ReimbursementSubmissions)}
      >
        &larr; Back to Reimbursement Submissions
      </BackLink>

      <Text variant='h4' marginBottom='1.5rem'>
        Request Details
      </Text>

      <DetailsCard>
        <DetailRow>
          <DetailLabel variant='body1'>Employee</DetailLabel>
          <Text variant='body1'>{employeeName}</Text>
        </DetailRow>
        <DetailRow>
          <DetailLabel variant='body1'>Merchant</DetailLabel>
          <Text variant='body1'>{receipt.merchant || '-'}</Text>
        </DetailRow>
        <DetailRow>
          <DetailLabel variant='body1'>Receipt Date</DetailLabel>
          <Text variant='body1'>
            {moment(receipt.receiptDatetime).format('MMM D, YYYY')}
          </Text>
        </DetailRow>
        <DetailRow>
          <DetailLabel variant='body1'>Receipt</DetailLabel>
          <Text variant='body1'>
            <ReceiptLink receiptUrl={receipt.receiptUrl} />
          </Text>
        </DetailRow>
        <DetailRow>
          <DetailLabel variant='body1'>Amount</DetailLabel>
          <Text variant='body1'>
            {formatCurrency('USD', receipt.amount, conversionRates)}
          </Text>
        </DetailRow>
        <DetailRow>
          <DetailLabel variant='body1'>Merchant Amount</DetailLabel>
          <Text variant='body1'>
            {formatCurrency(
              (receipt.merchantCurrency as CurrencyType) || 'USD',
              receipt.amount,
              conversionRates
            )}
          </Text>
        </DetailRow>
        <DetailRow>
          <DetailLabel variant='body1'>Approved Amount</DetailLabel>
          <Text variant='body1'>
            {receipt.approvedAmount != null
              ? formatCurrency(
                  (receipt.merchantCurrency as CurrencyType) || 'USD',
                  receipt.approvedAmount,
                  conversionRates
                )
              : '-'}
          </Text>
        </DetailRow>
        <DetailRow>
          <DetailLabel variant='body1'>Expense Type</DetailLabel>
          <Text variant='body1'>{formatExpenseType(receipt.expenseType)}</Text>
        </DetailRow>
        <DetailRow>
          <DetailLabel variant='body1'>Status</DetailLabel>
          <Chip variant={statusMap[receipt.status] || 'grey'}>
            {statusDisplayMap[receipt.status]}
          </Chip>
        </DetailRow>
        <DetailRow>
          <DetailLabel variant='body1'>Employee Note</DetailLabel>
          <Text variant='body1'>{receipt.employeeNote || '-'}</Text>
        </DetailRow>
        {shouldShowReimbursementBudgets(receipt) && (
          <ReimbursementBudgets
            receipt={receipt}
            budgetAllocations={budgetAllocations}
            onBudgetAllocationsChange={setBudgetAllocations}
            remainingReimbursementBalance={remainingReimbursementBalance}
          />
        )}
      </DetailsCard>

      <TextField
        placeholder='Add a note (optional)'
        value={note}
        onChange={(e) => setNote(e.target.value)}
        disabled={receipt.status !== 'CREATED'}
        style={{
          marginTop: '1.5rem',
          fontWeight: 600,
          backgroundColor: palette.grey.grey100,
          color: palette.text.primary,
        }}
      />

      <FlexContainer marginTop='1.5rem'>
        <Button
          disabled={isApproveDisabled(
            receipt,
            resolving,
            remainingReimbursementBalance,
            budgetAllocations
          )}
          marginRight='1rem'
          onClick={() => handleResolve('APPROVED')}
        >
          Approve
        </Button>
        <Button
          disabled={isPartialDisabled(
            receipt,
            resolving,
            remainingReimbursementBalance,
            budgetAllocations
          )}
          marginRight='1rem'
          onClick={() => handleResolve('PARTIAL')}
        >
          Partial
        </Button>
        <Button
          variant='cancel'
          disabled={receipt.status !== 'CREATED' || resolving}
          onClick={() => handleResolve('REJECTED')}
        >
          Reject
        </Button>
      </FlexContainer>
    </Container>
  )
})

if (process.env.NODE_ENV !== 'production') {
  ReimbursementDetailsView.displayName = 'ReimbursementDetailsView'
}

export default ReimbursementDetailsView
